import { ref, update, get } from 'firebase/database'
import { db } from './firebase.js'

// Surname-pair join key (inline copy of data.js normalizeTeamKey — kept local to
// avoid an import cycle). "Ratliff / Keese" → "ratliffkeese".
const normKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '')

// ── Per-team access tokens (TEST feature) ──────────────────────────────────
// Each team gets a UNIQUE, unguessable token. A deep link that carries the token
// (/play?t=<tid>&tk=<token>) opens the companion app bound to that team — no name
// or email needed, and the 128-bit random token can't be fabricated to reach
// another team. Stored two ways under the tournament:
//   flights/{fid}/teams/{teamId}/info/accessToken = <token>   (the team's token)
//   teamTokens/{token} = { flightId, teamId, teamName }        (reverse lookup)

// 128 bits of crypto randomness → 32 URL-safe hex chars. Unguessable.
export function generateTeamToken() {
  const bytes = new Uint8Array(16)
  ;(globalThis.crypto || window.crypto).getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Assign a token to every team that doesn't already have one, in a single
// atomic update. Existing tokens are left untouched (stable QR codes). Returns
// the number of tokens created.
export async function assignTeamTokens(tournamentId, flights) {
  const updates = {}
  let created = 0
  for (const f of flights || []) {
    for (const [teamId, team] of Object.entries(f.teams || {})) {
      if (team?.info?.accessToken) continue
      const token = generateTeamToken()
      updates[`flights/${f.id}/teams/${teamId}/info/accessToken`] = token
      updates[`teamTokens/${token}`] = { flightId: f.id, teamId, teamName: team?.info?.name || teamId }
      created++
    }
  }
  if (created > 0) await update(ref(db, `tournaments/${tournamentId}`), updates)
  return created
}

// The companion deep link for a team. The token is the secret; the tournament id
// is not (it just scopes the reverse lookup to a readable path).
export function teamPlayLink(origin, tournamentId, token) {
  return `${origin}/play?t=${encodeURIComponent(tournamentId)}&tk=${encodeURIComponent(token)}`
}

// Re-link-on-rebuild: after teams are (re)built or re-imported (new push-ids), a
// token whose stored teamId no longer exists is re-pointed to the CURRENT team by
// its surname-pair name — so an already-printed QR keeps resolving. Never nulls
// the node; only fixes stale teamId/flightId. Idempotent (no-op when all valid).
// Returns the number of tokens re-linked.
export async function relinkTeamTokens(tournamentId, flights) {
  const snap = await get(ref(db, `tournaments/${tournamentId}/teamTokens`))
  const tokens = snap.val() || {}
  const byName = new Map()
  const liveIds = new Set()
  for (const f of flights || []) {
    for (const [teamId, team] of Object.entries(f.teams || {})) {
      liveIds.add(teamId)
      const k = normKey(team?.info?.name)
      if (k && !byName.has(k)) byName.set(k, { flightId: f.id, teamId })
    }
  }
  const updates = {}
  let relinked = 0
  for (const [tk, v] of Object.entries(tokens)) {
    if (!v || typeof v !== 'object') continue
    if (v.teamId && liveIds.has(v.teamId)) continue          // still valid — leave it
    const hit = v.teamName ? byName.get(normKey(v.teamName)) : null
    if (hit && (hit.teamId !== v.teamId || hit.flightId !== v.flightId)) {
      updates[`teamTokens/${tk}/teamId`] = hit.teamId
      updates[`teamTokens/${tk}/flightId`] = hit.flightId
      relinked++
    }
  }
  if (relinked > 0) await update(ref(db, `tournaments/${tournamentId}`), updates)
  return relinked
}
